<template>
  <ListComponent title="Inbound Integration Logs">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @keyup.enter="searchLogs"
            @click:clear="() => { filters.search = ''; searchLogs(); }"
            class="mr-2"
          ></v-text-field>
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFiltersDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(filters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>

            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters class="ma-0">
              <v-col cols="12" class="font-weight-bold">
                Applied Filters
              </v-col>

              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>
          <Columns :headers="headers" :gridColInstance="gridColumnApi" />
        </v-col>

        <v-col cols="4" class="px-0 pt-0 pb-2">
          <v-tabs height="35" v-model="tab" centered>
            <v-tabs-slider></v-tabs-slider>
            <v-tab> Company </v-tab>
            <v-tab> Driver </v-tab>
            <v-tab> Driver Leave </v-tab>
            <v-tab> Asset Maintenance </v-tab>
            <v-tab> TOS Events </v-tab>
            <v-tab> CODECO </v-tab>
          </v-tabs>
        </v-col>

        <v-col
          cols="4"
          class="px-0 pt-0 pb-2 d-flex align-center justify-end"
        >
          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
            class="mr-2"
          />
          <v-btn
            v-if="tab === 5"
            color="primary"
            outlined
            small
            :loading="exporting"
            @click="exportCodecoInboundLogs"
          >
            <v-icon left small>mdi-file-export-outline</v-icon>
            Export
          </v-btn>
        </v-col>
      </v-row>

      <v-col cols="12" class="pa-0">
        <AgGridVue
          :key="tab"
          style="width: 100%; height: calc(100vh - 172px)"
          class="ag-theme-alpine"
          id="myGrid"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          @columnMoved="resizeGrid"
          @columnVisible="resizeGrid"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :suppressDragLeaveHidesColumns="true"
          :rowData="apiLogsList"
        ></AgGridVue>
      </v-col>
    </template>
    <template #dialogs>
      <InboundIntegrationLogFilters
        :bindingObject="filters"
        :currentTab="tab"
        v-model="showFiltersDialog"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(15)"
      />

      <v-dialog v-model="failedDialog" fullscreen scrollable>
        <v-card>
          <!-- Header -->
          <v-card-title class="failed-dialog-header d-flex align-center py-3 px-4">
            <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span class="font-weight-medium text-body-1">Failed Records</span>
            <v-chip v-if="failedBatch" small outlined class="ml-3">{{ failedBatch.ref_id }}</v-chip>
            <template v-if="failedBatch">
              <v-divider vertical class="mx-4" style="height:20px;" />
              <span class="text-caption grey--text mr-4">
                <v-icon x-small class="mr-1">mdi-format-list-numbered</v-icon>
                Total: <strong>{{ failedBatch.total_rows }}</strong>
              </span>
              <span class="text-caption success--text mr-4">
                <v-icon x-small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                Accepted: <strong>{{ failedBatch.accepted }}</strong>
              </span>
              <span class="text-caption error--text">
                <v-icon x-small color="error" class="mr-1">mdi-close-circle-outline</v-icon>
                Rejected: <strong>{{ failedBatch.rejected }}</strong>
              </span>
            </template>
            <v-spacer />
            <v-btn icon small @click="failedDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />

          <!-- Table -->
          <v-card-text class="pa-0" style="overflow-y:auto;">
            <v-simple-table>
              <template #default>
                <thead>
                  <tr class="failed-table-head">
                    <th class="text-left" style="width:60px;">Row</th>
                    <th class="text-left" style="width:150px;">BOE</th>
                    <th class="text-left" style="width:110px;">Status</th>
                    <th class="text-left">Errors</th>
                    <th class="text-center" style="width:70px;">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="record in failedList"
                    :key="record.id"
                    class="failed-table-row"
                  >
                    <td class="text-caption font-weight-medium">{{ record.row }}</td>
                    <td class="text-caption font-weight-medium">{{ record.boe || '-' }}</td>
                    <td>
                      <span
                        v-if="record.status && record.status.toLowerCase() === 'rejected'"
                        class="error-border px-2 py-1 error--text"
                        style="font-size:11px;white-space:nowrap;"
                      >Rejected</span>
                      <span
                        v-else
                        class="success-border px-2 py-1 success--text"
                        style="font-size:11px;white-space:nowrap;"
                      >Accepted</span>
                    </td>
                    <td>
                      <div
                        v-if="record.errors && record.errors.length"
                        class="failed-errors-list"
                      >
                        <div
                          v-for="(err, i) in record.errors"
                          :key="i"
                          class="failed-error-item"
                        >
                          <v-icon x-small color="error" class="mr-1">mdi-alert</v-icon>
                          <span v-if="err.field" class="font-weight-medium mr-1">{{ err.field }}:</span>
                          <span>{{ err.message || err }}</span>
                        </div>
                      </div>
                      <span v-else class="grey--text text-caption">—</span>
                    </td>
                    <td class="text-center">
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="primary"
                            v-on="on"
                            v-bind="attrs"
                            @click="editFailedRecord(record)"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit & Retry</span>
                      </v-tooltip>
                    </td>
                  </tr>
                  <tr v-if="!failedList.length">
                    <td colspan="5" class="text-center grey--text pa-6">
                      <v-icon color="grey lighten-1" large class="d-block mx-auto mb-2">mdi-check-circle-outline</v-icon>
                      No failed records found.
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import IntegrationLogButton from "@/components/AgGridComponents/IntegrationLogButton.vue";
import CodecoInboundLogButton from "@/components/AgGridComponents/CodecoInboundLogButton.vue";
import InboundIntegrationLogFilters from "@/components/ModuleBased/dialogs/filters/InboundIntegrationLogFilters.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,
    AgGridVue,
    Pagination,
    agColumnHeader: CustomHeader,
    DetailsParent,
    IntegrationLogButton,
    CodecoInboundLogButton,
    InboundIntegrationLogFilters,
  },
  watch: {
    tab() {
      this.pageNo = 1;
      this.apiCall();
    },
  },
  data() {
    return {
      sorting: {},
      showFiltersDialog: false,
      exporting: false,
      apiLogsList: [],
      // pagination vars
      itemsPerPage: 20,
      pageNo: 1,
      totalItems: 0,

      // Filter vars
      filters: {},

      tab: null,
      permissions: null,

      // Failed records dialog
      failedDialog: false,
      failedBatch: null,
      failedList: [],

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    headers() {
      if (this.tab === 5) {
        return [
          {
            headerName: "Created On",
            field: "created",
            tooltipField: "created",
            sortable: true,
            minWidth: 150,
            valueFormatter: (params) => this.$globals.formatDate(params.value, true),
          },
          {
            headerName: "Event Type",
            field: "event",
            tooltipField: "event",
            minWidth: 140,
          },
          {
            headerName: "Container Number",
            field: "container_number",
            tooltipField: "container_number",
            minWidth: 160,
            valueGetter: (params) => {
              const data = (params.data && params.data.payload && params.data.payload.data) || [];
              return data.map((item) => item.container_number).filter(Boolean).join(", ");
            },
          },
          {
            headerName: "VBS Reference No",
            field: "vbs_reference_no",
            tooltipField: "vbs_reference_no",
            minWidth: 160,
            cellClass: (params) => (params.value ? "primary--text font-weight-bold cursor-pointer" : ""),
            valueGetter: (params) => {
              const data = (params.data && params.data.payload && params.data.payload.data) || [];
              return data.map((item) => item.vbs_reference_no).filter(Boolean).join(", ");
            },
            onCellClicked: (params) => {
              const firstVbs = (params.value || "").split(",")[0].trim();
              if (firstVbs) {
                this.$router.push({
                  path: "/app/admin/emergency-order-container/list",
                  query: { search: firstVbs },
                });
              }
            },
          },
          {
            headerName: "Terminal",
            field: "terminal",
            tooltipField: "terminal",
            minWidth: 120,
            valueGetter: (params) => (params.data && params.data.payload && params.data.payload.terminal) || "-",
          },
          {
            headerName: "Event Timestamp",
            field: "ts",
            tooltipField: "ts",
            minWidth: 170,
            valueFormatter: (params) => (params.value ? this.$globals.formatDate(params.value, true) : "-"),
          },
          {
            headerName: "Actions",
            field: "actions",
            cellRendererFramework: "CodecoInboundLogButton",
            pinned: "right",
            width: 100,
          },
        ];
      }
      if (this.tab === 4) {
        return [
          {
            headerName: "Created On",
            field: "created",
            tooltipField: "created",
            sortable: true,
            minWidth: 150,
            valueFormatter: (params) => this.$globals.formatDate(params.value, true),
          },
          {
            headerName: "Event Type",
            field: "event_type",
            tooltipField: "event_type",
            minWidth: 140,
          },
          {
            headerName: "VBS Reference No",
            field: "vbs_reference_number",
            tooltipField: "vbs_reference_number",
            minWidth: 160,
          },
          {
            headerName: "Integration ID",
            field: "integration_id",
            tooltipField: "integration_id",
            minWidth: 200,
          },
          {
            headerName: "Status",
            field: "is_success",
            minWidth: 120,
            cellRenderer: (params) => {
              return params.value
                ? `<span class="success-border px-2 py-1 success--text">Success</span>`
                : `<span class="error-border px-2 py-1 error--text">Failed</span>`;
            },
          },
          {
            headerName: "Response Code",
            field: "response_code",
            minWidth: 120,
          },
          {
            headerName: "Actions",
            field: "actions",
            cellRendererFramework: "IntegrationLogButton",
            pinned: "right",
            width: 100,
          },
        ];
      }
      return [
        {
          headerName: "Created On",
          field: "created",
          tooltipField: "created",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => this.$globals.formatDate(params.value, true),
        },
        {
          headerName: "Method",
          field: "method",
          tooltipField: "method",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Detail",
          field: "detail",
          tooltipField: "detail",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Status",
          field: "status",
          minWidth: 120,
          cellRenderer: function (params) {
            const code = params.data?.response_code;
            return code && /^2/.test(code.toString())
              ? `<span class="success-border px-2 py-1 success--text"> Success </span>`
              : `<span class="error-border px-2 py-1 error--text"> Failed </span>`;
          },
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "IntegrationLogButton",
          pinned: "right",
          width: 70,
        },
      ];
    },
    currentProfile() {
      let obj = JSON.parse(localStorage.getItem("userProfile"));
      if (!obj) {
        return null;
      } else {
        return obj;
      }
    },

    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  methods: {
    apiCall() {
      if (this.tab === 0) {
        this.getCompanyIntegrationLogs();
      } else if (this.tab === 1) {
        this.getDriverIntegrationLogs();
      } else if (this.tab === 2) {
        this.getDriverLeaveIntegrationLogs();
      } else if (this.tab === 3) {
        this.getAssetMaintenanceIntegrationLogs();
      } else if (this.tab === 4) {
        this.getTosInboundLogs();
      } else if (this.tab === 5) {
        this.getCodecoInboundLogs();
      }
    },
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchLogs() {
      this.pageNo = 1;
      this.apiCall();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.apiCall();
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(15);
    },
    prevPage() {
      this.pageNo--;
      this.apiCall();
    },
    nextPage() {
      this.pageNo++;
      this.apiCall();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },

    getCompanyIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getCompanyIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getDriverIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getDriverIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getDriverLeaveIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getDriverLeaveIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getAssetMaintenanceIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getAssetMaintenanceIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    openFailedRecordsDialog(batchData) {
      this.failedBatch = batchData;
      this.failedList = [];
      this.failedDialog = true;
      this.$bus.$emit("showLoader", true);
      this.$api.integration
        .getBulkDARecordLogs({ batch: batchData.id, limit: "all" })
        .then((res) => {
          this.failedList = res.data.filter(r => r.status && r.status.toLowerCase() === 'rejected');
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    editFailedRecord(record) {
      this.failedDialog = false;
      this.$router.push({
        name: "app-admin-integration-logs-bulk-da-retry",
        params: { id: record.id },
      });
    },

    getBulkDAIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getBulkDAIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getTosInboundLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getTosInboundLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getCodecoInboundLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.emergencyOrderContainer
        .getCodecoInboundLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    exportCodecoInboundLogs() {
      this.exporting = true;
      const params = { ...this.filters };
      this.$api.emergencyOrderContainer
        .exportCodecoInboundLogs(params)
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "CODECO_Inbound_Logs.xlsx");
          document.body.appendChild(link);
          link.click();
          link.remove();
          this.exporting = false;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "No records found to export.",
            color: "error",
          });
          this.exporting = false;
        });
    },

    getIntegrationResponseBodyBlob(id) {
      this.$bus.$emit("showLoader", true);

      this.$api.integration
        .getIntegrationResponseBodyBlob(id)
        .then((resp) => {
          let jsonString = JSON.parse(resp.data);
          let blob = new Blob([jsonString], {
            type: "application/json",
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "inboundintegration" + ".json";
          link.click();
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("integration");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.apiCall();
      }
    }
  },
};
</script>

<style>
.success-border {
  border: 1px solid #4caf50;
  border-radius: 30px;
}
.error-border {
  border: 1px solid #ff5252;
  border-radius: 30px;
}
.warning-border {
  border: 1px solid #fb8c00;
  border-radius: 30px;
}

.grey-border {
  border: 1px solid #9e9e9e;
  border-radius: 30px;
}
.failed-dialog-header {
  background-color: #fafafa;
  border-bottom: none;
}
.failed-table-head th {
  background-color: #f9f9f9 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #555 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  padding: 10px 16px !important;
}
.failed-table-row td {
  padding: 10px 16px !important;
  border-bottom: 1px solid #f0f0f0 !important;
  vertical-align: top;
}
.failed-table-row:hover {
  background-color: #fafafa !important;
}
.failed-errors-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.failed-error-item {
  font-size: 12px;
  color: #d32f2f;
  display: flex;
  align-items: flex-start;
  line-height: 1.4;
}
</style>
